'use client';

import { c as _c } from "react/compiler-runtime";
import type { DirectPurchaseBinding, FundingQuoteReview } from '@morfi/onramp-sdk';
import { directPurchaseQuantity, directPurchaseSlippage, type FundingFeeEstimate, type FundingLimit, formatFiatUnits, fundingAllowanceUsedPercent, fundingFeeEstimateForAmount, gatewayDisclosure, isValidFundingFeeEstimate, selectFundingAllowance } from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/app-accordion';
import { Skeleton } from '@/components/ui/app-skeleton';
import { logger } from '@/lib/telemetry/logger';
import { FundingAllowance } from '../funding-allowance';
export function FundingFees(t0) {
  const $ = _c(50);
  const {
    currency,
    decimals,
    principalUnits,
    estimate,
    purchase,
    purchaseReview,
    limits: t1,
    loading: t2,
    indicative: t3,
    direction,
    walletDebitUnits
  } = t0;
  const limits = t1 === undefined ? [] : t1;
  const loading = t2 === undefined ? false : t2;
  const indicative = t3 === undefined ? false : t3;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const [open, setOpen] = useState("");
  let t4;
  if ($[0] !== estimate) {
    t4 = estimate !== null && !isValidFundingFeeEstimate(estimate);
    $[0] = estimate;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const invalid = t4;
  let t5;
  let t6;
  if ($[2] !== invalid) {
    t5 = () => {
      if (invalid) {
        logger.warn("onramp.fee_estimate_invalid", {
          code: "invalid_fee_sum"
        });
      }
    };
    t6 = [invalid];
    $[2] = invalid;
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  const current = fundingFeeEstimateForAmount(estimate, currency, decimals, principalUnits);
  const summaryLimit = selectFundingAllowance(limits);
  let t7;
  if ($[5] !== t) {
    t7 = t("limit_daily_used");
    $[5] = t;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t) {
    t8 = t("limit_monthly_used");
    $[7] = t;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t) {
    t9 = t("limit_yearly_used");
    $[9] = t;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = {
      daily: t7,
      monthly: t8,
      yearly: t9
    };
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const limitLabels = t10;
  let t11;
  if ($[15] !== currency || $[16] !== decimals || $[17] !== locale) {
    t11 = function money(value) {
      return formatFiatUnits(value, decimals, currency, locale);
    };
    $[15] = currency;
    $[16] = decimals;
    $[17] = locale;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const money = t11;
  let t12;
  if ($[19] !== locale || $[20] !== purchase || $[21] !== purchaseReview || $[22] !== t) {
    t12 = purchase && purchaseReview && <div className="flex flex-col gap-2 text-caption"><p>{t("you_receive")}:{" "}{directPurchaseQuantity(purchase, purchaseReview.receivedUnits, locale)}</p><p>{t("direct_purchase_slippage")}: {directPurchaseSlippage(purchaseReview, locale)} ·{" "}{gatewayDisclosure.slippageExplanation}</p><p>{gatewayDisclosure.beforeLink}<a href={gatewayDisclosure.link} target="_blank" rel="noopener noreferrer" className="underline">{gatewayDisclosure.linkLabel}</a>{gatewayDisclosure.afterLink}</p></div>;
    $[19] = locale;
    $[20] = purchase;
    $[21] = purchaseReview;
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = summaryLimit ? <AnimatedProgressCircle label={limitLabels[summaryLimit.period]} progress={fundingAllowanceUsedPercent(summaryLimit)} diameter={20} strokeWidth={2} strokeClassName="stroke-foreground-brand" trackClassName="stroke-foreground-brand" /> : null;
  let t14;
  if ($[24] !== t) {
    t14 = t("fees_and_limits");
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13 || $[27] !== t14) {
    t15 = <span className="inline-flex items-center gap-2">{t13}{t14}</span>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t) {
    t16 = t("estimated_fees");
    $[29] = t;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <dt>{t16}</dt>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== direction || $[34] !== t) {
    t18 = direction === "withdraw" ? t("wallet_debit") : t("total_charged");
    $[33] = direction;
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18) {
    t19 = <dt>{t18}</dt>;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = (purchase ? purchaseReview?.estimatedOutput : current?.estimatedOutput) ? t("estimated_receive") : t("you_receive");
  let t21;
  if ($[38] !== t20) {
    t21 = <dt>{t20}</dt>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = "flex items-center justify-end";
  const t23 = purchase ? current && purchaseReview ? directPurchaseQuantity(purchase, purchaseReview.receivedUnits, locale) : t("fees_pending") : current ? money(current.receivedLocalUnits ?? current.principalUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending");
  let t24;
  if ($[40] !== t23) {
    t24 = <dd className={t22}>{t23}</dd>;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== currency || $[43] !== decimals || $[44] !== summaryLimit) {
    t25 = summaryLimit ? <FundingAllowance limit={summaryLimit} currency={currency} decimals={decimals} /> : null;
    $[42] = currency;
    $[43] = decimals;
    $[44] = summaryLimit;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== current || $[47] !== indicative || $[48] !== t) {
    t26 = indicative && current ? <p className="text-caption text-foreground-secondary">{t("quote_estimate_notice")}</p> : null;
    $[46] = current;
    $[47] = indicative;
    $[48] = t;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  return <>{t12}<Accordion type="single" collapsible={true} value={open} onValueChange={next => {
      if (!next || current) {
        setOpen(next);
      }
    }} className="text-caption [&_[data-slot=accordion-content]]:motion-reduce:animate-none" aria-busy={loading && !current}><AccordionItem value="fees" className="border-0"><AccordionTrigger disabled={!current && !open} className="items-center gap-2 py-2.5 text-caption font-normal text-foreground-secondary hover:no-underline disabled:opacity-100"><span className="inline-flex w-full items-center justify-between gap-3">{t15}<span className="flex min-w-16 items-center justify-end gap-2">{current ? money(current.feeUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</span></span></AccordionTrigger><AccordionContent className="pb-0"><div className="flex flex-col gap-2 rounded-control bg-background-tertiary/40 p-3"><dl className="grid grid-cols-2 gap-2">{t17}<dd className="flex items-center justify-end">{current ? money(current.feeUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</dd>{t19}<dd className="flex items-center justify-end">{current ? money(direction === "withdraw" && walletDebitUnits ? walletDebitUnits : current.totalUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</dd>{t21}{t24}</dl>{t25}{t26}</div></AccordionContent></AccordionItem></Accordion></>;
}